import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Select, TextInput } from "flowbite-react";
import AutoCompleteInput from "../inputs/Autocomplete";
import AttachmentPicker from "../inputs/AttachmentPicker";
import TiptapEditor from "../inputs/TipTapEditor";
import { HiMicrophone } from "react-icons/hi";
import axios from "../../config/axiosInstance";
import { API } from "../../config/apiConfig";
import { hasHtmlTags, toRichHtml } from "../../utils/utils";

// ===== MODAL DE EDIÇÃO DE TAREFA =====
export default function TaskEditModal({
  open,
  onClose,
  taskBeingEdited, // objeto da tarefa sendo editada (t)
  editingData, // { editType, editNoteHtml, editDueDate, editDueTime, editResponsibleIds }
  setEditType,
  setEditNoteHtml,
  setEditDueDate,
  setEditDueTime,
  setEditResponsibleIds,
  userOptions,
  loadingUsers,
  saveEditTask,
  cancelEditTask, // limpa estados locais
  onDeleteAttachment, // remover anexo existente
  editFiles, // novos anexos selecionados na edição
  setEditFiles, // setter dos novos anexos
  labelMap,
  prettySize,
  fileIconFor,
}) {
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const dictationAbortRef = useRef(false);
  const [dictating, setDictating] = useState(false);
  const [dictationLoading, setDictationLoading] = useState(false);
  const [dictationTranscribing, setDictationTranscribing] = useState(false);
  const dictationTimerRef = useRef(null);
  const dictationSupported =
    typeof window !== "undefined" &&
    navigator?.mediaDevices?.getUserMedia &&
    window.MediaRecorder;

  const appendTranscript = useCallback((prev, transcript) => {
    const clean = String(transcript || "").trim();
    if (!clean) return prev || "";
    const current = String(prev || "");
    if (!current) return toRichHtml(clean);
    if (hasHtmlTags(current)) return `${current}${toRichHtml(clean)}`;
    const sep = current.endsWith("\n") ? "" : "\n";
    return `${current}${sep}${clean}`;
  }, []);

  const normalizeMimeType = useCallback((value = "") => {
    return String(value || "").split(";")[0].trim();
  }, []);

  const extFromMime = useCallback(
    (value = "") => {
      const type = normalizeMimeType(value).toLowerCase();
      if (!type) return "webm";
      if (type.includes("webm")) return "webm";
      if (type.includes("ogg") || type.includes("oga")) return "ogg";
      if (type.includes("mp4")) return "mp4";
      if (type.includes("mpeg") || type.includes("mp3")) return "mp3";
      if (type.includes("wav")) return "wav";
      if (type.includes("flac")) return "flac";
      if (type.includes("m4a")) return "m4a";
      return "webm";
    },
    [normalizeMimeType]
  );

  const clearDictationTimer = useCallback(() => {
    if (dictationTimerRef.current) {
      clearTimeout(dictationTimerRef.current);
      dictationTimerRef.current = null;
    }
    setDictationLoading(false);
  }, []);

  const cleanupStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    mediaStreamRef.current = null;
  }, []);

  const stopDictation = useCallback(
    (options = {}) => {
      const { discard = false } = options;
      clearDictationTimer();
      if (discard) dictationAbortRef.current = true;
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch (err) {
          console.warn("Falha ao parar a gravação:", err);
        }
      } else {
        dictationAbortRef.current = false;
        cleanupStream();
        setDictating(false);
      }
    },
    [clearDictationTimer, cleanupStream]
  );

  const pickMimeType = useCallback(() => {
    if (typeof window === "undefined" || !window.MediaRecorder) return "";
    const types = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
    ];
    return types.find((t) => MediaRecorder.isTypeSupported(t)) || "";
  }, []);

  const transcribeAudio = useCallback(
    async (blob) => {
      setDictationTranscribing(true);
      try {
        const fd = new FormData();
        const safeMime = normalizeMimeType(blob.type);
        const ext = extFromMime(safeMime);
        fd.append("file", blob, `audio.${ext}`);
        const res = await axios.post(API.TRANSCRIBE, fd, {
          headers: { "Content-Type": undefined },
        });
        const text = res?.data?.text || res?.data?.data?.text || "";
        if (!text) {
          alert("Não foi possível transcrever o áudio.");
          return;
        }
        setEditNoteHtml((prev) => appendTranscript(prev, text));
      } catch (err) {
        console.error("Erro ao transcrever áudio:", err);
        alert(
          err?.response?.data?.message || "Falha ao transcrever áudio."
        );
      } finally {
        setDictationTranscribing(false);
      }
    },
    [appendTranscript, extFromMime, normalizeMimeType, setEditNoteHtml]
  );

  const startDictation = useCallback(async () => {
    if (!dictationSupported) {
      alert("Seu navegador não suporta gravação de áudio. Use Chrome ou Edge.");
      return;
    }
    if (mediaRecorderRef.current) stopDictation({ discard: true });

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      console.warn("Erro ao acessar microfone:", err);
      alert("Permissão de microfone negada.");
      return;
    }

    mediaStreamRef.current = stream;
    audioChunksRef.current = [];
    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(
      stream,
      mimeType ? { mimeType } : undefined
    );

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    recorder.onerror = (event) => {
      console.warn("Erro na gravação de áudio:", event);
    };

    recorder.onstop = async () => {
      const discard = dictationAbortRef.current;
      dictationAbortRef.current = false;
      const chunks = audioChunksRef.current;
      audioChunksRef.current = [];
      cleanupStream();
      if (mediaRecorderRef.current === recorder) {
        mediaRecorderRef.current = null;
      }
      setDictating(false);
      if (discard || !chunks.length) return;
      const safeMime = normalizeMimeType(recorder.mimeType || "audio/webm");
      const blob = new Blob(chunks, {
        type: safeMime || "audio/webm",
      });
      await transcribeAudio(blob);
    };

    mediaRecorderRef.current = recorder;
    setDictating(true);
    recorder.start();
  }, [
    cleanupStream,
    dictationSupported,
    pickMimeType,
    stopDictation,
    transcribeAudio,
  ]);

  const startDictationWithDelay = useCallback(() => {
    if (dictationLoading || dictationTranscribing) return;
    clearDictationTimer();
    if (mediaRecorderRef.current) stopDictation({ discard: true });
    setDictationLoading(true);
    dictationTimerRef.current = setTimeout(() => {
      setDictationLoading(false);
      startDictation();
    }, 1500);
  }, [
    clearDictationTimer,
    dictationLoading,
    dictationTranscribing,
    startDictation,
    stopDictation,
  ]);

  useEffect(() => () => stopDictation({ discard: true }), [stopDictation]);

  if (!open || !taskBeingEdited) return null;

  const t = taskBeingEdited;

  return (
    <Modal show={open} onClose={onClose} size="3xl">
      <Modal.Header className="py-2 flex items-center">
        <span className="text-base">Editar tarefa</span>
      </Modal.Header>
      <Modal.Body>
        <div className="grid grid-cols-1 gap-4">
          {/* Tipo */}
          <div>
            <label className="text-xs text-gray-600 block mb-1">Tipo</label>
            <Select
              value={editingData.editType}
              onChange={(e) => setEditType(e.target.value)}
            >
              <option value="">—</option>
              {Object.entries(labelMap).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </div>

          {/* Descrição (TipTap) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-gray-600 block">Descrição</label>
              <button
                type="button"
                onClick={
                  dictating ? stopDictation : () => startDictationWithDelay()
                }
                disabled={
                  !dictationSupported || dictationLoading || dictationTranscribing
                }
                title={
                  dictationSupported
                    ? dictating
                      ? "Parar gravação"
                      : dictationLoading
                      ? "Preparando gravação"
                      : dictationTranscribing
                      ? "Transcrevendo áudio"
                      : "Voz -> Texto"
                    : "Gravação de áudio indisponível"
                }
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                  dictating
                    ? "border-red-300 bg-red-50 text-red-700"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                } ${!dictationSupported ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <HiMicrophone size={14} />
                {dictationLoading
                  ? "Carregando…"
                  : dictationTranscribing
                  ? "Transcrevendo…"
                  : dictating
                  ? "Parar"
                  : "Voz -> Texto"}
              </button>
            </div>
            <TiptapEditor
              value={editingData.editNoteHtml}
              onChange={setEditNoteHtml}
              placeholder="Descreva a tarefa…"
              minHeight={140}
            />
          </div>

          {/* Anexos existentes (com remover) */}
          {Array.isArray(t.attachments) && t.attachments.length > 0 && (
            <div>
              <label className="text-xs text-gray-600 block mb-1">Anexos</label>
              <ul className="space-y-1">
                {t.attachments.map((a) => {
                  const name =
                    a.originalName ||
                    a.filename ||
                    a.name ||
                    a.objectName ||
                    a.gcsKey ||
                    "arquivo";
                  const size = a.size ? ` • ${prettySize(a.size)}` : "";
                  const icon = fileIconFor(
                    a.mimeType || a.mimetype || "",
                    name
                  );
                  return (
                    <li
                      key={a.id || a.objectName || a.gcsKey || name}
                      className="flex items-center gap-2"
                    >
                      <span className="shrink-0">{icon}</span>
                      <span
                        className="text-sm text-gray-800 truncate"
                        title={name}
                      >
                        {name}
                      </span>
                      <span className="text-gray-400 text-xs">{size}</span>
                      <button
                        type="button"
                        className="ml-auto text-xs text-red-600 hover:underline"
                        onClick={() => onDeleteAttachment?.(t, a)}
                        title="Excluir anexo"
                      >
                        Remover
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Novos anexos */}
          <AttachmentPicker
            label="Adicionar novos anexos"
            files={editFiles}
            onChange={setEditFiles}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
          />

          {/* Data / Hora */}
          <div className="grid grid-cols-2 gap-2">
            <TextInput
              type="date"
              value={editingData.editDueDate}
              onChange={(e) => setEditDueDate(e.target.value)}
            />
            <input
              type="time"
              value={editingData.editDueTime || ""}
              onChange={(e) => setEditDueTime(e.target.value)}
              step="300" /* 5 min = 300s; ajuste se quiser 900 (15min) */
              className="w-full border rounded-md p-2 text-sm"
            />
          </div>

          {/* Responsáveis */}
          <div>
            <label className="text-xs text-gray-600 block mb-1">
              Responsáveis
            </label>
            <AutoCompleteInput
              label=""
              name={`edit-task-resp-${t.id}`}
              options={userOptions}
              value={editingData.editResponsibleIds || []}
              isMulti
              onChange={(opts) =>
                setEditResponsibleIds(
                  (opts || []).map((opt) => opt?.value).filter(Boolean)
                )
              }
              isLoading={loadingUsers}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="w-full flex justify-end gap-2">
          <Button
            size="sm"
            color="light"
            onClick={() => {
              cancelEditTask();
              onClose();
            }}
          >
            Cancelar
          </Button>
        </div>
        <Button
          size="sm"
          onClick={async () => {
            await saveEditTask(t); // já usa editFiles/estado atual
            onClose();
          }}
        >
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
